import { selectSearchFlightsForm } from "../searchFlightsSlice";
import { useAppSelector } from "../../../app/hooks";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { DirectionTypes, SearchFlightsForm, TimeSelect } from "../../../types";
import { getLocale } from "../../../utils";
import React, { useEffect, useState } from "react";
import {
  afternoonLabel,
  anyTimeLabel,
  eveningLabel,
  morningLabel,
} from "./utils";
import { getAirportSuggestionLabel } from "../../../utils";

function SearchFlightsFormContent() {
  const form = useAppSelector(selectSearchFlightsForm);
  const dateFormat = useAppSelector(
    (state) => state.config.components.dateFormat
  );
  const requestTimeFormat = useAppSelector(
    (state) => state.config.components.Inputs.RequestTime.format
  );
  const timeFormat = useAppSelector(
    (state) => state.config.components.timeFormat
  );
  const direction = useAppSelector(
    (state) => state.searchFlights.results.direction
  );
  const { t, i18n } = useTranslation();

  const [isOutbound, setIsOutbound] = useState<boolean>(
    direction === DirectionTypes.Outbound || direction === null
  );
  const [locale, setLocale] = useState<Locale>();

  useEffect(() => {
    setIsOutbound(direction === DirectionTypes.Outbound || direction === null);
  }, [direction]);

  useEffect(() => {
    const newLocale = getLocale(i18n.language);
    setLocale(newLocale);
  }, [i18n.language]);

  return (
    <div id="search-form-content" className="columns is-multiline">
      <div className="column is-half">
        <div className="field is-horizontal">
          <div className="field-label">
            <label className="label">{t("HEADER_LABEL_FROM_CAPS")}</label>
          </div>
          <div className="field-body">
            <span>
              {isOutbound
                ? getAirportSuggestionLabel(form.originAirport)
                : getAirportSuggestionLabel(form.destinationAirport)}
            </span>
          </div>
        </div>
      </div>
      {form.destinationAirport && (
        <div className="column is-half">
          <div className="field is-horizontal">
            <div className="field-label">
              <label className="label">{t("HEADER_LABEL_TO_CAPS")}</label>
            </div>
            <div className="field-body">
              <span>
                {isOutbound
                  ? getAirportSuggestionLabel(form.destinationAirport)
                  : getAirportSuggestionLabel(form.originAirport)}
              </span>
            </div>
          </div>
        </div>
      )}
      <div className="column is-half">
        <div className="field is-horizontal">
          <div className="field-label">
            <label className="label">{t("HEADER_LABEL_DEP_DATE_CAPS")}</label>
          </div>
          <div id="search-form-content-dep-date" className="field-body">
            <span>
              {generateDateLabel(form, isOutbound, dateFormat, i18n.language)}
            </span>
          </div>
        </div>
      </div>
      {requestTimeFormat === "dropdown" && (
        <div className="column is-half">
          <div className="field is-horizontal">
            <div className="field-label">
              <label className="label">{t("DEP_DOT_TIME")}</label>
            </div>
            <div className="field-body">
              {isOutbound && (
                <span>
                  {form.departingTimeSelect === TimeSelect.AnyTime &&
                    `${t("ANY_TIME")} ${anyTimeLabel(locale, timeFormat)}`}
                  {form.departingTimeSelect === TimeSelect.Morning &&
                    `${t("MORNING")} ${morningLabel(locale, timeFormat)}`}
                  {form.departingTimeSelect === TimeSelect.Afternoon &&
                    `${t("AFTERNOON")} ${afternoonLabel(locale, timeFormat)}`}
                  {form.departingTimeSelect === TimeSelect.Evening &&
                    `${t("EVENING")} ${eveningLabel(locale, timeFormat)}`}
                </span>
              )}
              {!isOutbound && (
                <span>
                  {form.returningTimeSelect === TimeSelect.AnyTime &&
                    `${t("ANY_TIME")} ${anyTimeLabel(locale, timeFormat)}`}
                  {form.returningTimeSelect === TimeSelect.Morning &&
                    `${t("MORNING")} ${morningLabel(locale, timeFormat)}`}
                  {form.returningTimeSelect === TimeSelect.Afternoon &&
                    `${t("AFTERNOON")} ${afternoonLabel(locale, timeFormat)}`}
                  {form.returningTimeSelect === TimeSelect.Evening &&
                    `${t("EVENING")} ${eveningLabel(locale, timeFormat)}`}
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function generateDateLabel(
  form: SearchFlightsForm,
  isOutbound: boolean,
  dateFormat: string,
  language: string
) {
  const dateFrom = isOutbound ? form.departingDateFrom : form.returningDateFrom;
  const dateTo = isOutbound ? form.departingDateTo : form.returningDateTo;
  const locale = getLocale(language);
  dateFormat = locale?.code === "ar-SA" ? "dd MMMM yyyy" : dateFormat;

  if (dateFrom === dateTo) {
    return format(dateFrom, dateFormat, { locale });
  } else {
    return `${format(dateFrom, dateFormat, { locale })} - ${format(
      dateTo,
      dateFormat,
      { locale }
    )}`;
  }
}

export default SearchFlightsFormContent;
